import memoize from 'memoize-one';

import { formatCurrency } from 'utils/NumberHelper';

const currencyField = (title) => ({ format: memoize((value) => formatCurrency(value)), title });
const monthsField = (title) => ({ format: memoize((value) => `${value} months`), title });
const percentageField = (title, tooltip) => ({
  format: memoize((value) => `${value}%`),
  title,
  tooltip,
});
const stringField = (title) => ({ format: (value) => value, title });
const boolField = (title) => ({ format: memoize((value) => (value ? 'Yes' : 'Not')), title });
const selectField = (title, options) => ({
  format: (value) => options.find((option) => option?.value === value)?.label || '-',
  title,
});

const FieldsTooltip = Object.freeze({
  sweep: '<b>Sweep</b> represents the percentage of daily takings that goes towards repayment.',
  serviceFee:
    'The <b>service fee</b> is a percentage charge taken from monthly takings running your facility.',
  representativeAPR:
    'An annual percentage rate (<b>APR</b>) is the annual rate charged for borrowing representing the actual yearly cost of funds over the term of a loan.',
});

export const manualOfferFieldsConfig = {
  additionalFees: stringField('Additional Fees'),
  advanceAmount: currencyField('Advance Amount'),
  advanceRate: percentageField('Advance Rate'),
  contractLength: monthsField('Contract Length'),
  cost: currencyField('cost'),
  deposit: currencyField('Deposit'),
  description: { title: 'Description' },
  discountFee: percentageField('Discount Fee'),
  facility: selectField('Facility', [
    {
      label: 'Discounting',
      value: 'DISCOUNTING',
    },
    {
      label: 'Factoring',
      value: 'FACTORING',
    },
  ]),
  fundingAmount: currencyField('Funding Amount'),
  likelihoodOfAcceptance: percentageField('Likelihood of Acceptance'),
  loanAmount: currencyField('Advance Amount'),
  maximumAmount: currencyField('Amount'),
  maximumCreditLine: currencyField('Maximum Credit Line '),
  monthlyRepayment: currencyField('Minimum Monthly Repayment'),
  productOption: selectField('Product Option', [
    {
      label: 'Hire Purchase',
      value: 'HIRE_PURCHASE',
    },
    {
      label: 'Finance Lease',
      value: 'FINANCE_LEASE',
    },
  ]),
  ratio: { title: 'Ratio' },
  representativeAPR: percentageField('APR', FieldsTooltip.representativeAPR),
  serviceFee: percentageField('Service Fee', FieldsTooltip.serviceFee),
  sweep: percentageField('Sweep', FieldsTooltip.sweep),
  term: monthsField('Term'),
  totalPrice: currencyField('Total Price'),
  totalRepayment: currencyField('Total Repayment'),
  vat: currencyField('VAT'),
};

export const fieldsConfig = {
  ...manualOfferFieldsConfig,
  arrangementFee: currencyField('Arrangement Fee'),
  creditInsuranceRequired: boolField('Credit Insurance Required'),
  exitFee: currencyField('Exit Fee'),
  ltv: percentageField('LTV'),
  minimumMonthlyFee: currencyField('Minimum Monthly Fee'),
  mortgageTerm: monthsField('Mortgage Term'),
  selective: boolField('Selective'),
  totalFees: currencyField('Total Fees'),
  valuationFee: currencyField('Valuation Fee'),
};

const filterFields = (key, fields) => fields.includes(key);

export const filterOptions = memoize((productData, fields, title = '') => {
  const filteredOption = Object.entries(productData).find(([key]) => filterFields(key, fields));

  if (!filteredOption) {
    return {
      config: {
        title,
      },
    };
  }

  const [optionKey, optionValue] = filteredOption;
  return {
    optionValue,
    key: optionKey,
    config: fieldsConfig[optionKey],
  };
});

export const borrowCostFields = ['deposit', 'representativeAPR', 'serviceFee', 'totalRepayment'];

export const summaryFields = [
  ['maximumAmount', 'maximumCreditLine', 'fundingAmount', 'advanceAmount'],
  borrowCostFields,
  ['contractLength', 'term'],
];

export const feesFields = [
  ['arrangementFee', 'discountFee', 'ratio', 'productOption'],
  ['totalRepayment', 'advanceRate', 'totalFees', 'cost', 'rateType'],
  ['additionalFees', 'vat', 'ltv'],
  ['minimumMonthlyFee', 'totalPrice', 'mortgageTerm'],
  ['description', 'arrangementFee'],
  ['facility', 'valuationFee'],
  ['exitFee'],
];
